import { useEffect, useState, useRef, type FormEvent } from 'react';
import ReactDOM from 'react-dom';
import { MoreHorizontal, Repeat2, ShieldAlert, Trash2, UserRoundX, UserRoundCheck, X } from 'lucide-react';
import { AppUser } from '../../../types';
import { deleteMediaItemFromDb } from '../../../backend/mediaService';
import { blockUser, reportContent, subscribeToBlockedUsers, unblockUser } from '../../../backend/moderationService';

interface ContentActionsMenuProps {
  contentId: string;
  contentType: 'photo' | 'short' | 'long';
  contentOwnerUid?: string;
  contentTitle?: string;
  contentThumbUrl?: string;
  currentUser?: AppUser | null;
  onOpenAuth?: () => void;
  onDeleted?: (contentId: string) => void;
  onReshare?: () => void;
}

const reportReasons = ['Spam', 'Harassment', 'Inappropriate content', 'Copyright concern', 'Other'];

export function ContentActionsMenu({
  contentId,
  contentType,
  contentOwnerUid,
  contentTitle,
  contentThumbUrl,
  currentUser,
  onOpenAuth,
  onDeleted,
  onReshare
}: ContentActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState(reportReasons[0]);
  const [details, setDetails] = useState('');
  const [message, setMessage] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isOwner = Boolean(currentUser?.uid && contentOwnerUid === currentUser.uid);
  const isSuperAdmin = Boolean(
    currentUser?.email === 'ajaytripathi821@gmail.com' ||
    currentUser?.role === 'superadmin' ||
    currentUser?.isSuperAdmin
  );
  const canDelete = isOwner || isSuperAdmin;
  const isBlocked = Boolean(contentOwnerUid && blockedUserIds.has(contentOwnerUid));
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    if (!currentUser?.uid) {
      setBlockedUserIds(new Set());
      return;
    }
    return subscribeToBlockedUsers(currentUser.uid, setBlockedUserIds);
  }, [currentUser?.uid]);

  // Handle outside click and Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const top = rect.bottom + 4;
      const right = window.innerWidth - rect.right;
      setCoords({ top, right });
    }
    setIsOpen(prev => !prev);
  };

  const handleDelete = async () => {
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (!canDelete || !window.confirm(`Delete "${contentTitle || 'this content'}" permanently?`)) return;
    setIsWorking(true);
    setMessage('');
    try {
      await deleteMediaItemFromDb(contentId, currentUser.uid);
      onDeleted?.(contentId);
      setIsOpen(false);
    } catch (error) {
      console.error('Could not delete content:', error);
      setMessage(error instanceof Error ? error.message : 'Could not delete this content.');
    } finally {
      setIsWorking(false);
    }
  };

  const handleBlock = async () => {
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (!contentOwnerUid || contentOwnerUid === currentUser.uid) return;
    setIsWorking(true);
    setMessage('');
    try {
      if (isBlocked) {
        await unblockUser(currentUser.uid, contentOwnerUid);
      } else {
        await blockUser(currentUser.uid, contentOwnerUid);
      }
      setIsOpen(false);
    } catch (error) {
      console.error('Could not update blocked users:', error);
      setMessage(error instanceof Error ? error.message : 'Could not update blocked users.');
    } finally {
      setIsWorking(false);
    }
  };

  const handleReport = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (!contentOwnerUid) {
      setMessage('The content owner could not be identified.');
      return;
    }
    setIsWorking(true);
    setMessage('');
    try {
      await reportContent(currentUser.uid, contentId, contentType, contentOwnerUid, reason, details, contentTitle, contentThumbUrl);
      setIsReportOpen(false);
      setIsOpen(false);
      setDetails('');
      setMessage('Report submitted.');
    } catch (error) {
      console.error('Could not submit content report:', error);
      setMessage(error instanceof Error ? error.message : 'Could not submit the report.');
    } finally {
      setIsWorking(false);
    }
  };

  const menuContent = (
    <div
      ref={menuRef}
      style={!isMobile && coords ? { top: Math.min(coords.top, window.innerHeight - 200), right: Math.max(12, coords.right) } : undefined}
      className={
        isMobile
          ? "fixed inset-x-0 bottom-0 z-[200] rounded-t-3xl border-t border-slate-200 bg-white p-4 shadow-2xl animate-in slide-in-from-bottom duration-200"
          : "fixed z-[200] min-w-44 rounded-xl border border-slate-200 bg-white p-1 shadow-2xl"
      }
      onClick={e => e.stopPropagation()}
    >
      {isMobile && (
        <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-3" />
      )}
      {onReshare && (
        <button
          type="button"
          disabled={isWorking}
          onClick={() => {
            onReshare();
            setIsOpen(false);
          }}
          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <Repeat2 className="h-4 w-4" /> Reshare
        </button>
      )}
      {canDelete ? (
        <button
          type="button"
          disabled={isWorking}
          onClick={() => void handleDelete()}
          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-xs font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" /> Delete
        </button>
      ) : (
        <>
          <button
            type="button"
            disabled={isWorking}
            onClick={() => {
              if (!currentUser) onOpenAuth?.();
              else {
                setIsReportOpen(true);
                setIsOpen(false);
              }
            }}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <ShieldAlert className="h-4 w-4" /> Report
          </button>
          {contentOwnerUid && (
            <button
              type="button"
              disabled={isWorking}
              onClick={() => void handleBlock()}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {isBlocked ? <UserRoundCheck className="h-4 w-4" /> : <UserRoundX className="h-4 w-4" />}
              {isBlocked ? 'Unblock user' : 'Block user'}
            </button>
          )}
        </>
      )}
      {message && <p role="status" className="px-3 py-2 text-[11px] text-slate-600">{message}</p>}
    </div>
  );

  return (
    <div className="relative inline-block" onClick={event => event.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        aria-label="Content actions"
        aria-expanded={isOpen}
        className="rounded-full p-2 text-slate-500 hover:bg-slate-100 transition-colors"
      >
        <MoreHorizontal className="h-5 w-5" />
      </button>

      {isOpen && ReactDOM.createPortal(
        <>
          {isMobile && (
            <div className="fixed inset-0 z-[190] bg-black/50 backdrop-blur-xs animate-in fade-in" onClick={() => setIsOpen(false)} />
          )}
          {menuContent}
        </>,
        document.body
      )}

      {isReportOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/60 p-4">
          <form onSubmit={event => void handleReport(event)} className="w-full max-w-sm space-y-4 rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black uppercase tracking-wider">Report content</h3>
              <button type="button" aria-label="Close report form" onClick={() => setIsReportOpen(false)} className="rounded-full p-2 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <label className="block text-xs font-bold text-slate-600">
              Reason
              <select value={reason} onChange={event => setReason(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm">
                {reportReasons.map(value => <option key={value}>{value}</option>)}
              </select>
            </label>
            <label className="block text-xs font-bold text-slate-600">
              Details (optional)
              <textarea value={details} onChange={event => setDetails(event.target.value)} maxLength={1000} rows={3} className="mt-1 w-full resize-none rounded-xl border border-slate-200 p-3 text-sm" />
            </label>
            {message && <p role="alert" className="text-xs text-rose-600">{message}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setIsReportOpen(false)} className="rounded-xl bg-slate-100 px-4 py-2 text-xs font-bold text-slate-700">Cancel</button>
              <button type="submit" disabled={isWorking} className="rounded-xl bg-blue-900 px-4 py-2 text-xs font-bold text-white disabled:opacity-50">{isWorking ? 'Sending…' : 'Submit report'}</button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
}
